import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  SimpleGrid,
  Icon,
  Flex,
  List,
  ListItem,
  ListIcon,
  Divider,
  Badge,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { NavbarWithCallToAction } from '../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../../brandConfig';
import { FaServer, FaCheckCircle, FaArrowRight, FaCoins, FaShieldAlt, FaChartLine, FaNetworkWired, FaLock } from 'react-icons/fa';
import { GiReceiveMoney } from 'react-icons/gi';
import { MdSecurity } from 'react-icons/md';
import ListBuildingModal from '../../components/ListBuildingModal';

export const RunANode = () => {
  const { colorMode } = useColorMode();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const nodeOptions = [
    {
      icon: FaServer,
      title: 'Full Node',
      cost: '$52,000 USD',
      description: 'Run a complete validator node with full collateral. Requires technical expertise and infrastructure.',
      color: '#3b82f6',
      benefits: [
        'Maximum control over your node',
        'Full validator rewards',
        'Vote on governance proposals',
        'Support network decentralization'
      ]
    },
    {
      icon: FaCoins,
      title: 'Stake on a Node',
      cost: 'Flexible Amount',
      description: 'Purchase B52 tokens and delegate your stake to an existing validator. No technical knowledge required.',
      color: '#64ffda',
      benefits: [
        'No technical setup required',
        'Start with any amount of B52',
        'Earn proportional rewards',
        'Undelegate anytime (with unbonding period)'
      ]
    }
  ];

  const validatorResponsibilities = [
    'Maintain 99.9% uptime to avoid slashing',
    'Validate transactions and blocks accurately',
    'Participate in network governance',
    'Upgrade node software for protocol improvements',
    'Monitor network health and performance',
    'Secure private keys and infrastructure'
  ];

  const rewardSources = [
    {
      source: 'Network Security Fee',
      description: 'A small percentage (1-3%) of every game\'s value secured on the network is distributed to validators who verify and protect transactions'
    },
    {
      source: 'Transaction Fees',
      description: 'Gas fees paid by users for blockchain transactions'
    },
    {
      source: 'Block Rewards',
      description: 'Network incentives for producing and validating blocks'
    }
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            {/* Header */}
            <VStack spacing={4} align="start" w="full">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                LEARN
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Run a Node
              </Heading>
              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} lineHeight="tall">
                Validators are the backbone of the Block52 network. Learn how to run your own node or stake tokens
                on an existing validator to earn rewards and help secure the network.
              </Text>
            </VStack>

            <Divider />

            {/* What is a Validator Node */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                What is a Validator Node?
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Validators are special nodes that process transactions, validate game states, and produce new blocks
                on the Block52 blockchain. They're essential for the network's security and decentralization.
              </Text>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                When you run a validator, you're staking B52 tokens as collateral. If you validate transactions correctly
                and maintain high uptime, you earn rewards. If you misbehave or go offline, your staked tokens can be
                "slashed" (partially forfeited) as a penalty.
              </Text>
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="#3b82f6"
              >
                <Flex align="center" mb={2}>
                  <Icon as={MdSecurity} color="#3b82f6" boxSize={6} mr={3} />
                  <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Security Through Incentives
                  </Heading>
                </Flex>
                <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                  The stake-and-slash model aligns validator incentives with network health. Validators are financially
                  motivated to act honestly because misbehavior results in loss of staked capital.
                </Text>
              </Box>
            </Box>

            {/* Two Ways to Participate */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={6} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Two Ways to Participate
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {nodeOptions.map((option, index) => (
                  <Box
                    key={index}
                    p={6}
                    borderRadius="lg"
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    border="2px solid"
                    borderColor={colorMode === 'dark' ? `${option.color}50` : option.color}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 16px ${option.color}30`,
                    }}
                  >
                    <Flex align="center" mb={4}>
                      <Flex
                        w="56px"
                        h="56px"
                        borderRadius="lg"
                        bg={`${option.color}20`}
                        align="center"
                        justify="center"
                        mr={4}
                      >
                        <Icon as={option.icon} color={option.color} boxSize={8} />
                      </Flex>
                      <Box>
                        <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                          {option.title}
                        </Heading>
                        <Text fontSize="lg" fontWeight="bold" color={option.color}>
                          {option.cost}
                        </Text>
                      </Box>
                    </Flex>
                    <Text color={getColor('text.secondary', colorMode)} fontSize="sm" mb={4}>
                      {option.description}
                    </Text>
                    <Divider mb={3} />
                    <VStack align="start" spacing={2}>
                      {option.benefits.map((benefit, idx) => (
                        <Flex key={idx} align="center">
                          <Icon as={FaCheckCircle} color={option.color} mr={2} fontSize="sm" />
                          <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                            {benefit}
                          </Text>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Earnings & Rewards */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                How Validators Earn
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Validators and their delegators earn rewards from multiple sources on the Block52 network:
              </Text>
              <VStack spacing={4} align="start">
                {rewardSources.map((reward, index) => (
                  <Box
                    key={index}
                    p={5}
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    borderRadius="lg"
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                    w="full"
                  >
                    <Flex align="start">
                      <Flex
                        w="32px"
                        h="32px"
                        borderRadius="full"
                        bg={index % 2 === 0 ? 'rgba(59, 130, 246, 0.2)' : 'rgba(100, 255, 218, 0.2)'}
                        align="center"
                        justify="center"
                        mr={4}
                        flexShrink={0}
                      >
                        <Icon as={GiReceiveMoney} color={index % 2 === 0 ? '#3b82f6' : '#64ffda'} boxSize={4} />
                      </Flex>
                      <Box>
                        <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                          {reward.source}
                        </Heading>
                        <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                          {reward.description}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </Box>

            {/* Validator Responsibilities */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Validator Responsibilities
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Running a validator node is a serious commitment. Validators must maintain high uptime and correct
                behavior to avoid penalties:
              </Text>
              <List spacing={3}>
                {validatorResponsibilities.map((responsibility, index) => (
                  <ListItem key={index}>
                    <Flex align="center">
                      <ListIcon as={FaCheckCircle} color="#64ffda" fontSize="xl" />
                      <Text color={getColor('text.secondary', colorMode)}>{responsibility}</Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Apply to Run a Node */}
            <Box
              w="full"
              p={6}
              bgGradient={colorMode === 'dark'
                ? 'linear(to-r, rgba(59, 130, 246, 0.1), rgba(100, 255, 218, 0.1))'
                : 'linear(to-r, blue.50, teal.50)'}
              borderRadius="xl"
              border="2px solid"
              borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6'}
            >
              <Flex align="center" mb={4}>
                <Icon as={FaServer} color="#3b82f6" boxSize={8} mr={3} />
                <Heading as="h2" size="lg" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Apply to Run a Validator
                </Heading>
              </Flex>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Interested in running a validator node? We're currently accepting applications from qualified
                candidates. Whether you want to run a full node or stake tokens on an existing validator, we'll
                help you get started.
              </Text>
              <Button
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                }}
                onClick={() => setIsModalOpen(true)}
              >
                Apply Now
              </Button>
            </Box>

            {/* Running a Test Node */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Running a Test Node
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Want to experiment with running a node before committing? You can run a validator on the Block52
                testnet. Here's a basic overview:
              </Text>

              <Alert
                status="info"
                variant="left-accent"
                borderRadius="lg"
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderColor="#3b82f6"
              >
                <AlertIcon color="#3b82f6" />
                <Box>
                  <AlertTitle color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Testnet Instructions
                  </AlertTitle>
                  <AlertDescription color={getColor('text.secondary', colorMode)}>
                    Full documentation for setting up a testnet validator will be available soon. For now, reach out
                    to our team and we'll guide you through the process.
                  </AlertDescription>
                </Box>
              </Alert>
            </Box>

            {/* CTA */}
            <Box w="full" textAlign="center" py={6}>
              <Button
                size="lg"
                px={8}
                py={6}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
                }}
                rightIcon={<FaArrowRight />}
                borderRadius="full"
                fontWeight="bold"
                onClick={() => setIsModalOpen(true)}
              >
                Start Now
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
      <ListBuildingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default RunANode;
